import React from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import GalleryViewer from "../components/GalleryViewer";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function TermografiKomfortundersoegelse() {
  const findings = [
    {
      title: "Træk og utætheder omkring vinduer og døre",
      desc: "Lokaliserer utætte fuger, manglende tætningslister eller skævheder i karme og rammer, hvor kold udeluft trænger ind.",
      icon: "fa-door-open",
    },
    {
      title: "Luftindtrængning ved gulve, vægge og gennemføringer",
      desc: "Afslører skjulte utætheder langs fodpaneler, etageskel, rørgennemføringer og samlinger i bygningskroppen.",
      icon: "fa-wind",
    },
    {
      title: "Kuldebroer og kolde overflader",
      desc: "Kortlægger byggetekniske svagheder og kuldebroer ved fundament, sokkel, hjørner og bjælkelag, der skaber kolde flader.",
      icon: "fa-snowflake",
    },
    {
      title: "Områder med mulig mangelfuld eller uens isolering",
      desc: "Viser felter i vægge, skunke eller loftrum, hvor isoleringen mangler, er faldet sammen eller er lagt mangelfuldt.",
      icon: "fa-layer-group",
    },
    {
      title: "Utætheder i boligens klimaskærm",
      desc: "Finder lækager i dampspærre og loftskonstruktion, som forårsager unødigt energitab og ubehagelig træk.",
      icon: "fa-house-crack",
    },
    {
      title: "Årsager til, at enkelte rum opleves koldere end andre",
      desc: "Kortlægger de bagvedliggende bygningsfysiske årsager til, at specifikke værelser eller zoner føles rå og fodkolde.",
      icon: "fa-temperature-low",
    },
  ];

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="Føles dit hus koldt? Termografi & komfortundersøgelse | ET Danmark"
        description="Føles dit hus koldt selvom der er 22 grader? Med en kombineret BlowerDoor trykprøvning og termografi lokaliserer vi træk, utætheder, kuldebroer og manglende isolering. Fra 5.888 kr. inkl. moms."
        canonical="https://www.etdanmark.com/termografi-komfortundersoegelse"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg"
      />

      {/* 1. TOP HERO */}
      <PageTopSlider
        title="Termografi & komfortundersøgelse"
        subtitle="Træk, kolde gulve og kolde overflader kan gøre en bolig ubehagelig at opholde sig i, selvom termostaten viser en helt normal temperatur. Med en termografi- og komfortundersøgelse fra ET Danmark undersøger vi, hvor problemet kommer fra."
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Ydelser", to: "/#ydelser" },
          { label: "Termografi & komfortundersøgelse" },
        ]}
        priceBadge="Fra 5.888 kr. inkl. moms"
        actions={
          <>
            <a
              href="#kontakt"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true"></i>
              <span>Bestil undersøgelse</span>
            </a>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Ring 42 49 95 95</span>
            </a>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg",
          alt: "Termografi og komfortundersøgelse med varmekamera og BlowerDoor",
        }}
      />

      {/* 2. PROBLEM & LØSNING INTRO */}
      <section className="py-14 sm:py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight leading-tight">
                Føles dit hus koldt – selvom der er 22 grader?
              </h2>

              <div className="space-y-4 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Træk, kolde gulve og kolde overflader kan gøre en bolig ubehagelig at opholde sig i, selvom termostaten viser en helt normal temperatur.
                </p>
                <p className="font-semibold text-stone-800">
                  Med en termografi- og komfortundersøgelse fra ET Danmark undersøger vi, hvor problemet kommer fra.
                </p>
                <p>
                  Vi kombinerer termografering med BlowerDoor, så vi både kan undersøge boligens tæthed og lokalisere områder, hvor kold luft trænger ind. Samtidig undersøger vi klimaskærmen for kuldebroer, kolde overflader og områder, der kan indikere mangelfuld eller uens isolering.
                </p>
              </div>

              {/* Fremhævet boks */}
              <div className="p-4 sm:p-5 rounded-2xl bg-[#11463A] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
                <div>
                  <span className="text-xs uppercase text-emerald-300 font-semibold block">Pris for undersøgelsen:</span>
                  <span className="text-xl sm:text-2xl font-bold text-white">Fra 5.888 kr. inkl. moms</span>
                  <span className="text-xs text-stone-200 block mt-0.5">Inkl. BlowerDoor, lækagesøgning, termografi & rapport</span>
                </div>
                <div className="flex gap-2">
                  <a
                    href="#kontakt"
                    className="px-5 py-2.5 rounded-full bg-white text-stone-900 font-bold text-xs hover:bg-stone-100 transition-colors whitespace-nowrap"
                  >
                    Kontakt os
                  </a>
                  <a
                    href="tel:+4542499595"
                    className="px-5 py-2.5 rounded-full bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-500 transition-colors whitespace-nowrap"
                  >
                    Ring 42 49 95 95
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-stone-100 bg-stone-100 aspect-4/3">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07508-2.jpg"
                  alt="Termografering og måling af overfladetemperaturer i bolig"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-3 left-3 right-3 bg-stone-950/85 backdrop-blur-xs text-white p-3 rounded-xl text-xs border border-white/10 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-600/30 text-emerald-400 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-camera text-sm" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="font-semibold block text-stone-100">Præcis lokalisering</span>
                    <span className="text-stone-300 text-[11px]">Kombinationen af undertryk og IR-kamera gør utætheder synlige</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. HVAD UNDERSØGELSEN KAN AFSLØRE */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Undersøgelsen kan blandt andet afsløre:
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2 leading-relaxed">
              Vi går systematisk til værks i hele boligen for at finde præcis de årsager, der skaber kulde, træk og ubalance i rumtemperaturen:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {findings.map((item, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-white border border-stone-200/90 shadow-2xs hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-100 flex items-center justify-center text-lg mb-4">
                    <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                  </div>
                  <h3 className="font-bold text-stone-900 text-base mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. VI GØR DET USYNLIGE SYNLIGT & RAPPORT */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-6">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Vi gør det usynlige synligt
              </h2>

              <div className="space-y-4 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Ved hjælp af et termografikamera kan temperaturforskelle i bygningen visualiseres. Når termografien kombineres med kontrolleret undertryk fra en BlowerDoor, kan luftlækager blive tydeligere og dermed lettere at lokalisere.
                </p>
                <p>
                  Det betyder, at vi ikke kun konstaterer, at boligen føles kold – vi undersøger de områder, der kan være årsagen.
                </p>
                <p>
                  Efter undersøgelsen modtager kunden en rapport med relevante termografibilleder, observationer og anbefalinger, så der er et konkret grundlag for eventuelle forbedringer.
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-stone-50 border border-stone-200">
                  <span className="text-emerald-700 font-bold text-base mt-0.5">✔️</span>
                  <div>
                    <span className="font-semibold text-stone-900 text-sm block">Termografikamera med høj opløsning</span>
                    <span className="text-xs text-stone-600">Visualiserer præcise temperaturforskelle på vægge, lofter og gulve.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-stone-50 border border-stone-200">
                  <span className="text-emerald-700 font-bold text-base mt-0.5">✔️</span>
                  <div>
                    <span className="font-semibold text-stone-900 text-sm block">Kontrolleret undertryk med BlowerDoor</span>
                    <span className="text-xs text-stone-600">Trækker udeluft ind gennem utætheder, så luftlækager tydeligt kan spores.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-stone-50 border border-stone-200">
                  <span className="text-emerald-700 font-bold text-base mt-0.5">✔️</span>
                  <div>
                    <span className="font-semibold text-stone-900 text-sm block">Udførlig rapport med konkrete anbefalinger</span>
                    <span className="text-xs text-stone-600">Giver dig et dokumenteret grundlag, som håndværkere eller du selv kan handle efter.</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-stone-100 bg-stone-100 aspect-4/3">
                <img
                  src="/cdn-cgi/image/width=900,quality=85,format=auto/_assets/dsc07506-2_5c7054e9.jpg"
                  alt="Termografibillede der viser temperaturforskelle og kuldebroer"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PRIS & HVAD ER INKLUDERET */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-3xl border-2 border-emerald-600/30 p-8 sm:p-10 shadow-lg relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-500/10 blur-2xl pointer-events-none" />

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-stone-200">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full inline-block mb-3">
                  Komplet pakke
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                  Termografi & komfortundersøgelse
                </h3>
                <p className="text-sm text-stone-600 mt-1">
                  Professionel fejlsøgning af kulde, træk og isoleringsmangler i boligen
                </p>
              </div>

              <div className="bg-stone-50 p-5 rounded-2xl border border-stone-200 text-center md:text-right shrink-0">
                <span className="text-xs text-stone-500 uppercase font-semibold block">Fast pris</span>
                <span className="text-3xl sm:text-4xl font-black text-stone-900">
                  Fra 5.888 kr.
                </span>
                <span className="text-xs text-emerald-800 font-semibold block mt-0.5">Inkl. moms</span>
              </div>
            </div>

            <div className="py-8 space-y-4">
              <p className="font-bold text-stone-900 text-sm">Prisen inkluderer:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-stone-700">
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs shrink-0">✓</span>
                  <span>BlowerDoor-trykprøvning</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs shrink-0">✓</span>
                  <span>Systematisk lækage- og træksøgning</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs shrink-0">✓</span>
                  <span>Termografering med IR-kamera</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs shrink-0">✓</span>
                  <span>Efterfølgende udførlig rapport</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs shrink-0">✓</span>
                  <span>Relevante termografibilleder</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs shrink-0">✓</span>
                  <span>Konkrete råd & forbedringsforslag</span>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-stone-500">
                Gælder enfamiliehuse i hele Jylland og Fyn. Kontakt os ved særlige ejendomme.
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <a
                  href="#kontakt"
                  className="flex-1 sm:flex-none text-center px-6 py-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-sm transition-colors"
                >
                  Bestil nu
                </a>
                <a
                  href="tel:+4542499595"
                  className="flex-1 sm:flex-none text-center px-6 py-3 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-900 font-bold text-xs transition-colors"
                >
                  Ring 42 49 95 95
                </a>
              </div>
            </div>
          </div>

          {/* TILVALG: FUGTMÅLING */}
          <div className="mt-4 p-4 rounded-2xl bg-white border border-stone-200 text-xs sm:text-sm text-stone-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
            <div className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                <i className="fa-solid fa-droplet text-xs" aria-hidden="true"></i>
              </span>
              <span>
                <strong>Tilvalg: Fugtmåling.</strong> Viser termografien kolde felter, der kan skyldes fugt, kan vi fugtmåle dem ved samme besøg. Pris efter tilbud.
              </span>
            </div>
            <Link
              to="/fugtmaaling"
              className="text-[#165B4C] hover:text-[#0f3f35] font-bold text-xs whitespace-nowrap inline-flex items-center gap-1 shrink-0"
            >
              <span>Læs mere</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. HAR DU SKRUET OP FOR VARMEN – MEN FRYSER STADIG? (CTA) */}
      <section className="py-14 sm:py-16 bg-[#11463A] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <div className="w-14 h-14 rounded-2xl bg-white/10 text-emerald-300 flex items-center justify-center mx-auto text-2xl border border-white/15">
            <i className="fa-solid fa-temperature-arrow-up" aria-hidden="true"></i>
          </div>

          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
            Har du skruet op for varmen – men fryser stadig?
          </h2>

          <p className="max-w-2xl mx-auto text-stone-200 text-sm sm:text-base leading-relaxed">
            Så behøver løsningen ikke nødvendigvis være mere varme. Kontakt ET Danmark og få undersøgt, hvor kulden eller trækket kommer fra.
          </p>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#kontakt"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-white text-stone-900 hover:bg-stone-100 font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope text-emerald-700 text-xs" aria-hidden="true"></i>
              <span>Kontakt ET Danmark</span>
            </a>
            <a
              href="tel:+4542499595"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Tlf. 42 49 95 95</span>
            </a>
          </div>
        </div>
      </section>

      {/* 7. CMS GALLERI FOR TERMOGRAFI */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <GalleryViewer
            gallerySlug="termografi"
            title="Galleri: Termografiske målinger"
            subtitle="Infrarøde billeder og visualisering af kuldebroer og varmetab"
          />
        </div>
      </section>

      {/* 8. REVIEWS SLIDER */}
      <ReviewsSlider />

      {/* 9. KONTAKT FORMULAR */}
      <section id="kontakt" className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full inline-block mb-3">
                  Direkte kontakt
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                  Kontakt ET Danmark
                </h2>
              </div>

              <div className="space-y-4 text-stone-600 text-sm leading-relaxed">
                <p>
                  Oplever du træk, kolde gulve eller uens rumtemperatur i din bolig? Vi står klar til at hjælpe dig med professionel rådgivning og hurtig udrykning i hele Jylland og på Fyn.
                </p>
                <p>
                  Ring direkte til os eller send en besked via formularen – vi vender hurtigt tilbage.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-stone-200/80 shadow-2xs space-y-4 text-sm text-stone-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 block">Ring til os:</span>
                    <a href="tel:+4542499595" className="font-bold text-stone-900 hover:text-emerald-700 text-base">
                      42 49 95 95
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 block">Send en email:</span>
                    <a href="mailto:info@etdanmark.com" className="font-bold text-stone-900 hover:text-emerald-700 text-base">
                      info@etdanmark.com
                    </a>
                  </div>
                </div>

                <div className="pt-2 border-t border-stone-100 text-xs text-stone-500 space-y-1">
                  <p className="font-semibold text-stone-800">ET Danmark ApS</p>
                  <p>Bjerrevej 62 A, 8700 Horsens</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm
                defaultService="Termografi"
                title="Bestil undersøgelse eller få et uforpligtende tilbud"
                subtitle="Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt med henblik på en gennemgang af din bolig."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
